// Exercise and advice hub articles for N S Physiotherapy Centurion.
// Organised into topic clusters: each pillar article is supported by
// focused articles that link back to it.

import type { BlogPost } from "./blog-types";

const UPDATED = "2026-08-12";

export const EXERCISE_HUB_POSTS: BlogPost[] = [
  // ===================== BACK PAIN CLUSTER =====================
  {
    slug: "back-pain-causes-treatment-and-physiotherapy-exercises",
    category: "Back Pain",
    isPillar: true,
    featured: true,
    publishedAt: "2026-08-01",
    updatedAt: UPDATED,
    title: "Back Pain: Causes, Treatment and Physiotherapy Exercises",
    metaTitle: "Back Pain: Causes, Treatment and Physio Exercises | Centurion",
    metaDescription:
      "A physiotherapist's guide to back pain: common causes, what treatment involves, safe exercises to start with and when to book an assessment in Centurion.",
    quickAnswer:
      "Most back pain is mechanical, meaning it comes from the joints, discs and muscles of the spine rather than a serious disease. Gentle movement, gradual strengthening and a return to normal activity usually help more than rest. If pain lasts longer than two weeks, keeps returning or travels down the leg, a physiotherapy assessment can identify the cause and guide the right exercises.",
    intro:
      "Back pain is the single most common reason people book a physiotherapy assessment at our Centurion clinic. The good news is that the large majority of episodes settle well when you understand what is driving the pain and load the spine sensibly. This guide explains the usual causes, what physiotherapy treatment involves and which exercises are a reasonable starting point.",
    tags: ["lower back pain", "backache", "lumbar", "spine", "core"],
    sections: [
      {
        heading: "What actually causes most back pain",
        body:
          "In clinical practice the majority of back pain is described as non specific mechanical back pain. That sounds vague, but it simply means the pain comes from irritated structures in the spine: the small facet joints, the discs, the ligaments or the muscles that control them. Common triggers include a sudden awkward lift, long hours seated with little movement, a rapid increase in gym or running load, and periods of poor sleep or high stress, both of which lower your pain threshold. Less commonly, pain is linked to a disc bulge irritating a nerve root, which usually produces leg symptoms as well.",
      },
      {
        heading: "What physiotherapy treatment involves",
        body:
          "A first session starts with a detailed interview, then a physical assessment of your movement, strength and nerve function. From there treatment is typically a combination of hands on techniques to reduce pain and restore movement, education about load and activity, and a graded exercise programme you continue at home. Dry needling, soft tissue work and joint mobilisation may reduce symptoms in the short term, but the lasting change comes from exercise and better daily loading habits.",
      },
      {
        heading: "Why rest is rarely the answer",
        body:
          "Extended bed rest tends to make back pain worse. Muscles deconditioned quickly, joints stiffen and confidence in movement drops. Current guidance across most health systems is to stay as active as your symptoms allow, modify rather than stop activity, and return to normal routines progressively. Walking is often the easiest starting point because it loads the spine gently and rhythmically.",
      },
      {
        heading: "Physiotherapy for back pain in Centurion",
        body:
          "If you are dealing with persistent back pain in Centurion, an assessment can separate a simple mechanical problem from one that needs imaging or a medical opinion. Our clinic is in Eco Park, Centurion, and we also do home visits across Rooihuiskraal, Irene, Lyttelton, Midstream, Wierdapark, Highveld and surrounding areas when travelling is difficult.",
      },
    ],
    exercises: [
      {
        name: "Cat Cow Stretch",
        startingPosition: "On your hands and knees, hands under shoulders and knees under hips.",
        steps: [
          "Breathe out and gently round your spine towards the ceiling.",
          "Breathe in and slowly let your spine sag towards the floor.",
          "Move slowly and stay inside a comfortable range.",
          "Keep your neck relaxed and in line with your spine.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Most people with stiff, achy backs that ease with movement.",
        safety: "Stop if the movement produces sharp pain or pins and needles into the legs.",
      },
      {
        name: "Glute Bridge",
        startingPosition: "Lying on your back, knees bent, feet flat and hip width apart.",
        steps: [
          "Breathe out and press through your heels to lift your hips.",
          "Lift only as far as you can without arching your lower back.",
          "Hold briefly at the top and squeeze your glutes.",
          "Lower slowly and reset before the next repetition.",
        ],
        reps: "10 repetitions",
        sets: "2 to 3 sets",
        difficulty: "Beginner",
        suitableFor: "Building hip and trunk strength to support the lower back.",
        safety: "Keep the effort in your buttocks, not your lower back or hamstrings cramping.",
      },
      {
        name: "Dead Bug",
        startingPosition: "Lying on your back, knees and hips bent to ninety degrees, arms towards the ceiling.",
        steps: [
          "Gently brace your trunk so your lower back stays steady.",
          "Slowly lower one arm overhead and the opposite leg towards the floor.",
          "Only go as far as you can without your back arching away from the floor.",
          "Return to the start and repeat on the other side.",
        ],
        reps: "6 to 8 per side",
        sets: "2 sets",
        difficulty: "Intermediate",
        suitableFor: "People ready to progress from basic mobility to control work.",
        safety: "Reduce the range if you feel your back lifting or pain increasing.",
      },
    ],
    whenToStop: [
      "Sharp, sudden or increasing pain during the movement.",
      "New or worsening pins and needles, numbness or weakness in a leg.",
      "Pain that stays noticeably worse for more than 24 hours afterwards.",
    ],
    whenToSeePhysio:
      "Book an assessment if pain lasts more than two weeks, keeps returning, wakes you at night, travels below the knee, or is limiting work, sleep or exercise. Seek urgent medical care for loss of bladder or bowel control, saddle numbness, unexplained weight loss or fever with back pain.",
    faqs: [
      { q: "Can exercise help lower back pain?", a: "For most mechanical back pain, yes. Graded movement and strengthening may reduce symptoms and lower the chance of recurrence, although the right exercises depend on what is driving your pain." },
      { q: "Should I rest if my back is sore?", a: "Short term relative rest is fine, but prolonged rest usually delays recovery. Staying gently active within comfort is generally better." },
      { q: "Do I need an X ray or MRI for back pain?", a: "Usually not. Imaging is reserved for cases with specific warning signs or symptoms that do not respond to treatment, because scan findings often do not match the pain." },
      { q: "How many physiotherapy sessions will I need?", a: "Many mechanical back pain episodes improve within four to six sessions alongside a home programme, but this varies with how long the problem has been present." },
    ],
    relatedServices: ["back-pain-physiotherapy", "dry-needling", "home-physiotherapy"],
    relatedPosts: [
      "exercises-for-lower-back-pain",
      "stretches-for-lower-back-pain",
      "back-pain-from-sitting",
      "exercises-for-sciatica",
    ],
  },
  {
    slug: "exercises-for-lower-back-pain",
    category: "Physiotherapy Exercises",
    featured: true,
    pillarSlug: "back-pain-causes-treatment-and-physiotherapy-exercises",
    publishedAt: "2026-08-02",
    updatedAt: UPDATED,
    title: "Exercises for Lower Back Pain",
    metaTitle: "Exercises for Lower Back Pain | Physio Guide Centurion",
    metaDescription:
      "Simple, physiotherapist explained exercises for lower back pain, with step by step instructions, suggested dosage and clear guidance on when to stop.",
    quickAnswer:
      "Start with gentle mobility such as cat cow and knee rolls, then add strength work such as glute bridges and bird dogs. Two short sessions a day, kept comfortable, is usually better than one hard session. If pain increases and stays worse, stop and get assessed.",
    intro:
      "When your lower back is sore, the hardest question is what you are actually allowed to do. This guide gives a practical starting programme used often in our Centurion clinic, with dosage and safety rules so you know exactly how much to do.",
    tags: ["lower back pain", "core exercises", "home programme"],
    sections: [
      {
        heading: "How to structure your first two weeks",
        body:
          "Begin with mobility exercises once or twice a day to reduce stiffness, then add two or three strengthening exercises every second day. Keep discomfort at a level you would describe as mild, roughly three out of ten, and expect it to settle within an hour of finishing. Progress by adding repetitions before you add difficulty.",
      },
      {
        heading: "Why strength matters more than stretching alone",
        body:
          "Stretching often feels good but the relief can be short lived. Strength work in the hips, trunk and back extensors changes how much load your spine can tolerate day to day, which is what keeps episodes from returning. In practice a blend works best: mobility first to reduce guarding, then strength to build capacity.",
      },
      {
        heading: "Getting help locally",
        body:
          "General exercises suit many people but not everyone. If you are working through back pain in Centurion and are not sure which exercises fit your presentation, a single physiotherapy assessment can turn a generic list into a specific plan.",
      },
    ],
    exercises: [
      {
        name: "Knee Rolls",
        startingPosition: "Lying on your back, knees bent, feet flat, arms out to the sides.",
        steps: [
          "Keep your knees together and let them roll slowly to one side.",
          "Only go as far as feels comfortable through your lower back.",
          "Return to the middle and roll to the other side.",
        ],
        reps: "8 to 10 per side",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Morning stiffness and general lower back tightness.",
        safety: "Keep the movement slow. Stop if pain travels into the leg.",
      },
      {
        name: "Bird Dog",
        startingPosition: "On hands and knees with a neutral spine.",
        steps: [
          "Reach one arm forwards and the opposite leg backwards.",
          "Keep your hips level and your trunk steady.",
          "Hold for two to three seconds, then return with control.",
          "Alternate sides.",
        ],
        reps: "6 to 8 per side",
        sets: "2 to 3 sets",
        difficulty: "Intermediate",
        suitableFor: "Improving trunk control once the sharpest pain has settled.",
        safety: "Lower the arm or leg only if your back stays still and pain free.",
      },
      {
        name: "Sit to Stand",
        startingPosition: "Seated towards the front of a firm chair, feet flat.",
        steps: [
          "Lean your chest slightly forwards over your feet.",
          "Press through your heels and stand up tall.",
          "Sit down slowly, controlling the last part of the movement.",
        ],
        reps: "8 to 12 repetitions",
        sets: "2 to 3 sets",
        difficulty: "Beginner",
        suitableFor: "Rebuilding functional leg and hip strength during a painful episode.",
        safety: "Use armrests for support if needed. Avoid holding your breath.",
      },
    ],
    whenToStop: [
      "Pain that becomes sharp or radiates below the knee.",
      "Numbness, tingling or weakness developing during the set.",
      "A flare up that lasts longer than a day after exercising.",
    ],
    whenToSeePhysio:
      "If two weeks of consistent exercise has not improved your symptoms, or the pain is limiting sleep and work, book a physiotherapy assessment so the programme can be matched to your specific diagnosis.",
    faqs: [
      { q: "How often should I do back exercises?", a: "Mobility work can be done daily. Strength work is usually done every second day to allow recovery, then progressed as symptoms settle." },
      { q: "Should I exercise if my back hurts?", a: "Mild discomfort during exercise is usually acceptable. Sharp pain, leg symptoms or a flare that lasts more than 24 hours means the exercise or the dosage needs changing." },
      { q: "How long before I feel a difference?", a: "Many people notice easier movement within one to two weeks, while strength related change usually takes six weeks or more." },
      { q: "Can physiotherapy help chronic back pain?", a: "It can. Chronic back pain often responds to a combination of graded exercise, education about pain and load, and hands on treatment for symptom relief." },
    ],
    relatedServices: ["back-pain-physiotherapy", "dry-needling"],
    relatedPosts: [
      "back-pain-causes-treatment-and-physiotherapy-exercises",
      "stretches-for-lower-back-pain",
      "exercises-for-sciatica",
      "back-pain-from-sitting",
    ],
  },
  {
    slug: "stretches-for-lower-back-pain",
    category: "Back Pain",
    pillarSlug: "back-pain-causes-treatment-and-physiotherapy-exercises",
    publishedAt: "2026-08-03",
    updatedAt: UPDATED,
    title: "Stretches for Lower Back Pain",
    metaTitle: "Stretches for Lower Back Pain | Physiotherapist Guide",
    metaDescription:
      "Gentle stretches that may ease lower back tightness, with clear instructions, hold times and guidance on when stretching is not the right answer.",
    quickAnswer:
      "Hold gentle stretches for 20 to 30 seconds, two to three times each, and aim for a comfortable pull rather than pain. Stretching may reduce tightness and help you move more freely, but it works best alongside strengthening.",
    intro:
      "Stretching is usually the first thing people try when their lower back feels tight. Done properly it can take the edge off. Done aggressively it can irritate an already sensitive back. Here is a sensible set of stretches and how to use them.",
    tags: ["stretching", "tight back", "flexibility"],
    sections: [
      {
        heading: "What stretching can and cannot do",
        body:
          "A stretch may reduce the protective muscle guarding around a sore area and give you a window of easier movement. It does not lengthen a muscle permanently in a single session and it will not resolve an underlying strength or loading problem. Use stretches as symptom relief and a warm up to your strengthening work.",
      },
      {
        heading: "Where the tightness often comes from",
        body:
          "Lower back tightness frequently involves the hip flexors, hamstrings and glutes, particularly in people who sit for long periods. Stretching only the back while the hips remain stiff often gives short lived relief, which is why the routine below includes the hips.",
      },
    ],
    exercises: [
      {
        name: "Single Knee to Chest",
        startingPosition: "Lying on your back with both knees bent.",
        steps: [
          "Bring one knee gently towards your chest using your hands.",
          "Keep the other foot flat on the floor.",
          "Hold and breathe normally, then lower slowly.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 to 3 each side",
        difficulty: "Beginner",
        suitableFor: "General lower back tightness and morning stiffness.",
        safety: "Ease off if you feel pinching in the hip or pain into the leg.",
      },
      {
        name: "Figure Four Glute Stretch",
        startingPosition: "Lying on your back, knees bent, one ankle crossed over the opposite thigh.",
        steps: [
          "Reach through and hold behind the supporting thigh.",
          "Draw the leg gently towards you until you feel a stretch in the buttock.",
          "Keep your head and shoulders relaxed on the floor.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 to 3 each side",
        difficulty: "Beginner",
        suitableFor: "Tight glutes contributing to back and hip stiffness.",
        safety: "Stop if you get nerve type pain travelling down the leg.",
      },
      {
        name: "Half Kneeling Hip Flexor Stretch",
        startingPosition: "Half kneeling with one knee down on a cushion and the other foot forward.",
        steps: [
          "Tuck your tailbone slightly under to flatten your lower back.",
          "Shift your weight gently forwards until you feel a stretch at the front of the hip.",
          "Keep your trunk upright rather than leaning back.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 each side",
        difficulty: "Beginner",
        suitableFor: "Desk workers and drivers with stiff hips.",
        safety: "Avoid arching your lower back to chase a bigger stretch.",
      },
    ],
    whenToStop: [
      "Any stretch that produces pain rather than a mild pull.",
      "Symptoms spreading further down the leg while stretching.",
      "Increasing soreness over several days of stretching.",
    ],
    whenToSeePhysio:
      "If stretching gives relief for only a few minutes and the tightness keeps returning, it usually points to a strength or loading issue that a physiotherapy assessment can identify.",
    faqs: [
      { q: "How long should I hold a back stretch?", a: "Twenty to thirty seconds is usually enough, repeated two to three times. Longer holds do not necessarily give better results." },
      { q: "Is it normal to feel worse after stretching?", a: "Mild soreness can happen, but pain that persists or spreads suggests the stretch was too strong or not appropriate for your problem." },
      { q: "Can I stretch every day?", a: "Gentle stretching is generally safe daily, provided symptoms settle quickly afterwards." },
      { q: "Do stretches replace strengthening?", a: "No. Stretching may reduce symptoms, while strengthening builds the capacity that helps prevent recurrence." },
    ],
    relatedServices: ["back-pain-physiotherapy", "home-physiotherapy"],
    relatedPosts: [
      "back-pain-causes-treatment-and-physiotherapy-exercises",
      "exercises-for-lower-back-pain",
      "back-pain-from-sitting",
    ],
  },
  {
    slug: "back-pain-from-sitting",
    category: "Workplace Ergonomics",
    pillarSlug: "back-pain-causes-treatment-and-physiotherapy-exercises",
    publishedAt: "2026-08-04",
    updatedAt: UPDATED,
    title: "Lower Back Pain From Sitting: Why It Happens and What Helps",
    metaTitle: "Lower Back Pain From Sitting | Desk Setup and Exercises",
    metaDescription:
      "Why sitting aggravates lower back pain, how to set up your desk, and short movement breaks that may reduce stiffness during a long working day.",
    quickAnswer:
      "Sitting itself is not damaging, but staying in one position for hours reduces blood flow and increases stiffness. Changing position every 30 to 45 minutes, adjusting chair and screen height, and adding short movement breaks usually helps more than buying an expensive chair.",
    intro:
      "Many of the office workers we see in Centurion describe the same pattern: comfortable in the morning, aching by mid afternoon, better after a walk. That pattern is typical of load related back pain from sustained sitting.",
    tags: ["desk", "office", "ergonomics", "sitting"],
    sections: [
      {
        heading: "Why the pain builds through the day",
        body:
          "When you sit still, the tissues of the lower back stay under a constant low level load. Blood flow reduces, the discs and joints receive less variation in pressure and the muscles that support your spine slowly fatigue. The body responds with stiffness and ache. This is a signal to move, not usually a sign of damage.",
      },
      {
        heading: "A workable desk setup",
        body:
          "Set your chair so your hips are slightly higher than your knees and your feet are supported. Bring the keyboard close enough that your elbows stay near your sides. Place the top of your screen roughly at eye level and about an arm's length away. If you use a laptop all day, add a separate keyboard and mouse so the screen can be raised.",
      },
      {
        heading: "The most useful habit: position changes",
        body:
          "The best posture is the next one. Set a reminder every 30 to 45 minutes to stand, walk to fill your water bottle or do a couple of standing back extensions. A sit stand desk helps only if you actually alternate between the two.",
      },
    ],
    exercises: [
      {
        name: "Standing Back Extension",
        startingPosition: "Standing tall with hands on your lower back.",
        steps: [
          "Gently lean backwards over your hands.",
          "Keep the movement small and comfortable.",
          "Return to upright and repeat.",
        ],
        reps: "5 to 10 repetitions",
        sets: "Every 30 to 45 minutes at your desk",
        difficulty: "Beginner",
        suitableFor: "Desk workers whose back stiffens during long sitting periods.",
        safety: "Stop if leaning back produces leg symptoms or dizziness.",
      },
      {
        name: "Seated Thoracic Rotation",
        startingPosition: "Sitting upright with arms crossed over your chest.",
        steps: [
          "Turn your upper body slowly to one side.",
          "Keep your hips facing forwards.",
          "Return through the middle and rotate the other way.",
        ],
        reps: "6 to 8 per side",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Mid back and lower back stiffness during the working day.",
        safety: "Move slowly and avoid forcing the end of the range.",
      },
    ],
    whenToStop: [
      "Pain that increases sharply with a movement break rather than easing.",
      "New leg symptoms while sitting or standing.",
    ],
    whenToSeePhysio:
      "If your back pain no longer settles overnight, or it now limits driving, sleeping or exercise, an assessment can identify which structures are sensitive and how to load them safely.",
    faqs: [
      { q: "Is sitting bad for my back?", a: "Prolonged unchanged sitting is the issue rather than sitting itself. Regular position changes make a bigger difference than any single chair." },
      { q: "Will a standing desk fix my back pain?", a: "It may help if you alternate positions, but standing all day can create its own symptoms. Variety is the goal." },
      { q: "How often should I get up from my desk?", a: "Every 30 to 45 minutes is a practical target, even if it is only for a minute or two." },
      { q: "Do lumbar support cushions help?", a: "Some people find them comfortable, particularly while driving, but they do not replace movement breaks and strengthening." },
    ],
    relatedServices: ["back-pain-physiotherapy", "neck-pain-physiotherapy"],
    relatedPosts: [
      "back-pain-causes-treatment-and-physiotherapy-exercises",
      "exercises-for-lower-back-pain",
      "neck-pain-from-computer-work",
      "exercises-for-forward-head-posture",
    ],
  },
  {
    slug: "back-pain-after-gym",
    category: "Back Pain",
    pillarSlug: "back-pain-causes-treatment-and-physiotherapy-exercises",
    publishedAt: "2026-08-05",
    updatedAt: UPDATED,
    title: "Back Pain After the Gym: Soreness or Injury?",
    metaTitle: "Back Pain After Gym | Physio Advice on What to Do Next",
    metaDescription:
      "How to tell normal training soreness from a back injury, what to do in the first 48 hours and how to return to lifting safely.",
    quickAnswer:
      "Muscle soreness usually appears within a day, feels dull and spread out, and eases with gentle movement. Injury related pain is often sharper, more localised, may limit specific movements and does not improve over several days. Reduce load rather than stopping training completely, and get assessed if symptoms persist beyond a week.",
    intro:
      "Training related back pain is common, particularly after a jump in load, a new exercise or a heavy deadlift session. Knowing the difference between soreness and something that needs attention determines what you should do next.",
    tags: ["gym", "deadlift", "training load", "muscle strain"],
    sections: [
      {
        heading: "Soreness versus injury",
        body:
          "Delayed onset muscle soreness typically peaks 24 to 48 hours after training, affects both sides fairly evenly and improves with light activity. Injury related pain tends to be more one sided, sharper with specific movements such as bending or twisting, and may have started during a particular lift.",
      },
      {
        heading: "The first 48 hours",
        body:
          "Keep moving within comfort, use heat or ice depending on which feels better, and avoid the specific movement that provoked the pain rather than avoiding all training. Working the upper body, walking or cycling gently keeps you active while the back settles.",
      },
      {
        heading: "Returning to lifting",
        body:
          "Restart at roughly half of your previous working load with excellent technique, and progress by about ten percent per week if symptoms stay settled. Pay attention to total weekly volume, not just the heaviest set, because rapid volume increases are a frequent trigger.",
      },
    ],
    whenToStop: [
      "Pain that worsens set by set during a session.",
      "Leg symptoms, numbness or weakness after a lift.",
      "Pain that has not improved at all after seven days.",
    ],
    whenToSeePhysio:
      "Book an assessment if you cannot return to normal training within one to two weeks, if the same injury keeps recurring, or if you want your lifting technique and programme reviewed.",
    faqs: [
      { q: "Should I stop training completely?", a: "Rarely. Most people do better by modifying load and exercise selection while keeping training frequency." },
      { q: "Is it safe to deadlift with back pain?", a: "Sometimes, at a reduced load and range. This is a good question for an individual assessment rather than a general rule." },
      { q: "How long does a back muscle strain take to heal?", a: "Mild strains often settle within two to three weeks, though returning to full load can take longer." },
      { q: "Does a belt prevent back injury?", a: "A belt can help brace at heavy loads, but it does not compensate for excessive weekly volume or poor technique." },
    ],
    relatedServices: ["sports-injury-physiotherapy", "back-pain-physiotherapy"],
    relatedPosts: [
      "back-pain-causes-treatment-and-physiotherapy-exercises",
      "exercises-for-lower-back-pain",
      "how-to-prevent-running-injuries",
    ],
  },

  // ===================== NECK PAIN CLUSTER =====================
  {
    slug: "neck-pain-causes-treatment-and-exercises",
    category: "Neck Pain",
    isPillar: true,
    featured: true,
    publishedAt: "2026-08-01",
    updatedAt: UPDATED,
    title: "Neck Pain: Causes, Treatment and Physiotherapy Exercises",
    metaTitle: "Neck Pain: Causes, Treatment and Exercises | Physio Centurion",
    metaDescription:
      "A physiotherapist's guide to neck pain, including common causes, treatment options, safe exercises and when to book an assessment in Centurion.",
    quickAnswer:
      "Most neck pain is mechanical and settles within a few weeks with gentle mobility, strengthening of the deep neck and shoulder blade muscles, and better daily positioning. Pain with arm symptoms, dizziness or after trauma should be assessed sooner.",
    intro:
      "Neck pain is one of the most common complaints we treat, particularly among people working long hours at a screen. This guide covers why it happens, what treatment usually involves and where to start with exercise.",
    tags: ["neck", "stiff neck", "cervical", "headache"],
    sections: [
      {
        heading: "Common causes",
        body:
          "Sustained positions, sudden movements, stress related muscle tension and reduced mid back mobility are the usual contributors. Occasionally an irritated nerve root causes pain, tingling or weakness into the arm, which changes the treatment approach. Neck pain and tension type headaches often occur together.",
      },
      {
        heading: "What treatment looks like",
        body:
          "Assessment covers neck and shoulder movement, muscle strength, nerve tests where indicated and the mid back. Treatment usually blends manual therapy and dry needling for short term relief with exercises for deep neck flexor endurance and shoulder blade strength, plus practical changes to how you sit and sleep.",
      },
      {
        heading: "Physiotherapy for neck pain in Centurion",
        body:
          "If neck pain in Centurion is affecting your work, sleep or driving, an assessment can identify whether the source is joint, muscular or nerve related and shape the programme accordingly.",
      },
    ],
    exercises: [
      {
        name: "Chin Nod",
        startingPosition: "Sitting or lying with your head supported and neck relaxed.",
        steps: [
          "Gently nod your chin down as if making a small double chin.",
          "Hold for five seconds while breathing normally.",
          "Relax fully before repeating.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Building endurance in the deep neck muscles.",
        safety: "Keep the movement small. Stop if it causes dizziness or arm symptoms.",
      },
      {
        name: "Upper Trapezius Stretch",
        startingPosition: "Sitting tall with one hand holding the side of the chair.",
        steps: [
          "Tilt your ear gently towards the opposite shoulder.",
          "Use your free hand for light overpressure only if comfortable.",
          "Keep your shoulders relaxed and down.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 to 3 each side",
        difficulty: "Beginner",
        suitableFor: "Tight, aching neck and shoulder muscles.",
        safety: "A gentle pull is enough. Never force the stretch.",
      },
      {
        name: "Shoulder Blade Squeeze",
        startingPosition: "Sitting or standing with arms relaxed at your sides.",
        steps: [
          "Draw your shoulder blades gently back and down.",
          "Hold for five seconds without shrugging.",
          "Release slowly.",
        ],
        reps: "10 repetitions",
        sets: "2 to 3 sets",
        difficulty: "Beginner",
        suitableFor: "Desk workers with neck and upper back fatigue.",
        safety: "Keep breathing and avoid over squeezing into pain.",
      },
    ],
    whenToStop: [
      "Dizziness, visual changes or nausea with neck movement.",
      "Pain, tingling or weakness spreading into the arm or hand.",
      "Sharp pain that stops the movement.",
    ],
    whenToSeePhysio:
      "See a physiotherapist if neck pain lasts longer than two weeks, keeps returning, follows a motor vehicle accident, or is associated with arm symptoms, severe headaches or dizziness.",
    faqs: [
      { q: "How long does neck pain usually last?", a: "Simple mechanical neck pain often improves noticeably within two to four weeks with appropriate exercise and activity changes." },
      { q: "Is it safe to exercise with neck pain?", a: "Gentle mobility and endurance exercises are generally safe. Exercises causing dizziness or arm symptoms should be stopped and assessed." },
      { q: "Can my pillow cause neck pain?", a: "Pillow height can contribute. Aim for support that keeps your neck in line with your spine in your usual sleeping position." },
      { q: "Does dry needling help neck pain?", a: "It may reduce muscle tension and pain in the short term for some people, and works best combined with exercise." },
    ],
    relatedServices: ["neck-pain-physiotherapy", "dry-needling"],
    relatedPosts: [
      "exercises-for-neck-pain",
      "neck-pain-from-computer-work",
      "exercises-for-forward-head-posture",
      "shoulder-pain-causes-treatment-and-exercises",
    ],
  },
  {
    slug: "exercises-for-neck-pain",
    category: "Physiotherapy Exercises",
    pillarSlug: "neck-pain-causes-treatment-and-exercises",
    publishedAt: "2026-08-02",
    updatedAt: UPDATED,
    title: "Exercises for Neck Pain and Tight Neck Muscles",
    metaTitle: "Exercises for Neck Pain | Physiotherapist Guided Routine",
    metaDescription:
      "A short physiotherapist guided routine of neck mobility, stretching and strengthening exercises, with dosage and safety guidance.",
    quickAnswer:
      "A useful daily routine combines gentle range of movement, one or two stretches for the upper trapezius and levator scapulae, and endurance work such as chin nods. Five to ten minutes once or twice a day is usually enough to start.",
    intro:
      "Neck exercises fail most often because they are done too hard, too fast or only when the pain is severe. This routine is deliberately gentle and designed to be repeatable at your desk or at home.",
    tags: ["neck exercises", "stretches", "stiff neck"],
    sections: [
      {
        heading: "Start with range of movement",
        body:
          "Slow rotations, side bends and gentle nods restore confidence in movement and reduce protective guarding. Move to the point of first resistance rather than into pain, and repeat little and often through the day.",
      },
      {
        heading: "Then add endurance and strength",
        body:
          "The deep neck flexors and the muscles around the shoulder blades fatigue quickly in people with recurring neck pain. Short holds repeated regularly build endurance better than heavy or prolonged effort.",
      },
    ],
    exercises: [
      {
        name: "Neck Rotation",
        startingPosition: "Sitting tall with shoulders relaxed.",
        steps: [
          "Turn your head slowly to look over one shoulder.",
          "Stop at the first point of tightness.",
          "Return through the middle and repeat to the other side.",
        ],
        reps: "5 to 8 per side",
        sets: "2 to 3 times daily",
        difficulty: "Beginner",
        suitableFor: "Acute stiff necks and general daily maintenance.",
        safety: "Never force the movement or use momentum.",
      },
      {
        name: "Levator Scapulae Stretch",
        startingPosition: "Sitting tall, one hand behind your back.",
        steps: [
          "Turn your head about 45 degrees away from the tight side.",
          "Look down towards your armpit.",
          "Apply very light overpressure with your other hand if comfortable.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 each side",
        difficulty: "Beginner",
        suitableFor: "Tightness at the base of the neck near the shoulder blade.",
        safety: "Ease off if you feel nerve type symptoms in the arm.",
      },
      {
        name: "Wall Angels",
        startingPosition: "Standing with your back against a wall, arms bent at shoulder height.",
        steps: [
          "Keep your forearms and hands as close to the wall as comfortable.",
          "Slide your arms slowly upwards.",
          "Lower with control to the starting position.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets",
        difficulty: "Intermediate",
        suitableFor: "Improving mid back and shoulder blade movement that supports the neck.",
        safety: "Reduce the range rather than arching your lower back.",
      },
    ],
    whenToStop: [
      "Dizziness or light headedness with neck movement.",
      "Increasing pain, tingling or weakness in the arm.",
    ],
    whenToSeePhysio:
      "If the routine does not change your symptoms within two weeks, or if pain radiates into the arm, arrange an assessment so treatment can be targeted.",
    faqs: [
      { q: "How often should I do neck exercises?", a: "Little and often works well: short sessions once or twice a day, particularly on desk based days." },
      { q: "Should neck exercises hurt?", a: "No. Mild stretch sensation is fine, but pain, dizziness or arm symptoms mean you should stop." },
      { q: "Can I use heat before exercising?", a: "Many people find a warm shower or heat pack for ten minutes makes the routine more comfortable." },
      { q: "When should I see a physiotherapist for neck pain?", a: "If pain persists beyond two weeks, keeps returning, or comes with arm symptoms, headaches or dizziness." },
    ],
    relatedServices: ["neck-pain-physiotherapy", "dry-needling"],
    relatedPosts: [
      "neck-pain-causes-treatment-and-exercises",
      "neck-pain-from-computer-work",
      "exercises-for-forward-head-posture",
    ],
  },
  {
    slug: "neck-pain-from-computer-work",
    category: "Workplace Ergonomics",
    pillarSlug: "neck-pain-causes-treatment-and-exercises",
    publishedAt: "2026-08-03",
    updatedAt: UPDATED,
    title: "Neck Pain From Working on a Computer",
    metaTitle: "Neck Pain From Computer Work | Setup and Exercise Guide",
    metaDescription:
      "Why screen work aggravates neck pain, how to adjust your workstation and quick exercises that may reduce stiffness during the working day.",
    quickAnswer:
      "Screen related neck pain is usually a load and duration problem. Raise the screen to eye level, bring the keyboard close, and interrupt sustained positions every 30 to 45 minutes with brief movement.",
    intro:
      "If your neck is comfortable on a Sunday and sore by Wednesday afternoon, the working environment is worth examining before anything else.",
    tags: ["computer", "laptop", "screen", "desk"],
    sections: [
      {
        heading: "The laptop problem",
        body:
          "A laptop forces a compromise: either the screen is too low or the keyboard is too high. Working like this for hours loads the neck extensors continuously. A laptop stand with an external keyboard and mouse solves most of it for very little cost.",
      },
      {
        heading: "Practical adjustments that help",
        body:
          "Position the top of the screen at roughly eye level, about an arm's length away. Support your forearms so the shoulders are not held up. Keep frequently used items within easy reach so you are not repeatedly twisting. If you take calls, use a headset instead of holding the phone against your shoulder.",
      },
      {
        heading: "Movement beats posture perfection",
        body:
          "There is no single perfect posture. What matters is that your position changes regularly. Set a recurring reminder and use one or two of the exercises below.",
      },
    ],
    exercises: [
      {
        name: "Desk Chin Tuck and Rotate",
        startingPosition: "Sitting upright, feet flat on the floor.",
        steps: [
          "Make a small chin tuck to lengthen the back of your neck.",
          "Hold the tuck and slowly rotate your head to one side.",
          "Return to the middle, release and repeat to the other side.",
        ],
        reps: "5 per side",
        sets: "Every hour of screen work",
        difficulty: "Beginner",
        suitableFor: "Desk based workers with mid afternoon neck stiffness.",
        safety: "Stop if you feel dizzy or get arm symptoms.",
      },
      {
        name: "Doorway Chest Stretch",
        startingPosition: "Standing in a doorway with forearms on the frame at shoulder height.",
        steps: [
          "Step gently forwards with one foot.",
          "Allow a comfortable stretch across the front of the chest.",
          "Keep your ribs down and avoid arching your back.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Rounded shoulder positions from long screen sessions.",
        safety: "Ease off with any shoulder pinching pain.",
      },
    ],
    whenToStop: ["Any exercise that produces dizziness, headache or arm symptoms."],
    whenToSeePhysio:
      "If workstation changes and exercises do not reduce your symptoms within a few weeks, an assessment can determine whether joints, muscles or nerve sensitivity is the main driver.",
    faqs: [
      { q: "Can bad posture really cause neck pain?", a: "Sustained positions matter more than posture shape. Long unchanged positions load tissues, which is why movement breaks help." },
      { q: "Should I use a standing desk?", a: "It can be useful if you alternate sitting and standing. Standing all day may create its own problems." },
      { q: "Do neck braces or collars help?", a: "Generally not for everyday mechanical neck pain, as they reduce movement and can prolong stiffness." },
      { q: "How high should my monitor be?", a: "Roughly with the top of the screen at eye level and about an arm's length away." },
    ],
    relatedServices: ["neck-pain-physiotherapy"],
    relatedPosts: [
      "neck-pain-causes-treatment-and-exercises",
      "exercises-for-forward-head-posture",
      "back-pain-from-sitting",
      "exercises-for-neck-pain",
    ],
  },
  {
    slug: "exercises-for-forward-head-posture",
    category: "Posture",
    pillarSlug: "neck-pain-causes-treatment-and-exercises",
    publishedAt: "2026-08-04",
    updatedAt: UPDATED,
    title: "Exercises for Forward Head Posture",
    metaTitle: "Forward Head Posture Exercises | Physio Guide Centurion",
    metaDescription:
      "What forward head posture means, whether it causes pain and a simple exercise routine to improve neck endurance and upper back mobility.",
    quickAnswer:
      "Forward head posture is common and is not automatically painful. Rather than trying to hold a perfect position, build endurance in the deep neck muscles, improve upper back mobility and change position often.",
    intro:
      "Forward head posture gets blamed for a lot of neck pain. The evidence is more nuanced: posture alone predicts pain poorly, but people with limited movement variety and low neck endurance do tend to struggle more.",
    tags: ["posture", "text neck", "upper back"],
    sections: [
      {
        heading: "What the research suggests",
        body:
          "Studies comparing head position with neck pain show only weak associations. Many people with a noticeably forward head position have no symptoms at all. What is more consistently linked to symptoms is reduced endurance in the neck and shoulder blade muscles, and prolonged static loading.",
      },
      {
        heading: "A sensible plan",
        body:
          "Work on three things: mobility through the upper back, endurance in the deep neck flexors, and strength around the shoulder blades. Then reduce how long you spend in any one position. This combination may improve comfort even if your posture does not visibly change.",
      },
    ],
    exercises: [
      {
        name: "Thoracic Extension Over a Towel Roll",
        startingPosition: "Lying on your back with a rolled towel across your mid back.",
        steps: [
          "Support your head with your hands.",
          "Gently extend backwards over the roll.",
          "Breathe out as you extend, then return to the start.",
        ],
        reps: "6 to 8 repetitions",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Stiff upper backs from prolonged sitting.",
        safety: "Keep the roll below the shoulder blades and avoid the lower back.",
      },
      {
        name: "Deep Neck Flexor Hold",
        startingPosition: "Lying on your back, head resting on the floor or a thin pillow.",
        steps: [
          "Make a gentle chin nod.",
          "Hold the position for ten seconds while breathing normally.",
          "Fully relax between repetitions.",
        ],
        reps: "8 to 10 holds",
        sets: "2 sets",
        difficulty: "Intermediate",
        suitableFor: "Building neck endurance for long desk days.",
        safety: "Stop if the front of your neck strains or shakes heavily.",
      },
      {
        name: "Prone Y Raise",
        startingPosition: "Lying face down with arms out in a Y position.",
        steps: [
          "Draw your shoulder blades gently down and back.",
          "Lift both arms a few centimetres off the floor.",
          "Hold briefly, then lower with control.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets",
        difficulty: "Intermediate",
        suitableFor: "Strengthening the lower shoulder blade muscles.",
        safety: "Keep your neck relaxed and avoid shrugging.",
      },
    ],
    whenToStop: [
      "Neck pain that increases during or after the routine.",
      "Any dizziness or arm symptoms.",
    ],
    whenToSeePhysio:
      "If neck symptoms persist despite four to six weeks of consistent work, a physiotherapy assessment can measure your neck endurance and identify what is limiting progress.",
    faqs: [
      { q: "Is forward head posture permanent?", a: "Posture is adaptable. Mobility and strength usually improve with training, although the visible change may be modest." },
      { q: "Does forward head posture cause headaches?", a: "It may contribute in some people, particularly alongside muscle tension and long static positions, but it is rarely the only factor." },
      { q: "How long until I notice a difference?", a: "Endurance changes typically become noticeable after four to six weeks of consistent practice." },
      { q: "Should I use a posture corrector brace?", a: "Braces give a reminder but do not build strength. They are best used sparingly alongside exercise, if at all." },
    ],
    relatedServices: ["neck-pain-physiotherapy"],
    relatedPosts: [
      "neck-pain-causes-treatment-and-exercises",
      "neck-pain-from-computer-work",
      "exercises-for-neck-pain",
    ],
  },

  // ===================== SCIATICA CLUSTER =====================
  {
    slug: "sciatica-causes-treatment-and-exercises",
    category: "Sciatica",
    isPillar: true,
    featured: true,
    publishedAt: "2026-08-01",
    updatedAt: UPDATED,
    title: "Sciatica: What It Is, Treatment and Physiotherapy Exercises",
    metaTitle: "Sciatica: Causes, Treatment and Exercises | Physio Centurion",
    metaDescription:
      "What sciatica actually is, how physiotherapy treats it, which exercises may help, which to avoid and when leg pain needs urgent attention.",
    quickAnswer:
      "Sciatica describes pain travelling along the sciatic nerve, usually from irritation of a nerve root in the lower back. Most cases improve over weeks with gradual movement, nerve mobility work and strengthening. Progressive weakness, numbness in the saddle area or loss of bladder control require urgent medical attention.",
    intro:
      "Leg pain that runs from the buttock down the back of the thigh is frightening, especially when it does not settle with rest. Understanding what sciatica is makes the recovery plan far less intimidating.",
    tags: ["sciatica", "nerve pain", "leg pain", "disc"],
    sections: [
      {
        heading: "What sciatica actually is",
        body:
          "Sciatica is a symptom, not a diagnosis. It describes pain travelling along the path of the sciatic nerve, most often caused by irritation or compression of a nerve root in the lower back, sometimes by a disc bulge, sometimes by age related narrowing. Occasionally the nerve is irritated deeper in the buttock. Symptoms can include burning pain, tingling, numbness or a heavy leg.",
      },
      {
        heading: "How physiotherapy helps",
        body:
          "Treatment focuses on reducing nerve sensitivity and restoring tolerance to movement. That may include positions that centralise symptoms towards the spine, nerve gliding exercises, hands on treatment for the lower back and hip, and a graded strengthening programme. Education matters too: knowing that most sciatica improves without surgery changes how people move.",
      },
      {
        heading: "Recovery timelines",
        body:
          "Many people notice symptoms moving out of the calf and foot back towards the buttock within a few weeks, which is generally a good sign. Full recovery can take six to twelve weeks and sometimes longer where symptoms have been present for months.",
      },
    ],
    exercises: [
      {
        name: "Sciatic Nerve Glide",
        startingPosition: "Sitting upright on a chair with a slight slouch avoided.",
        steps: [
          "Straighten one knee while gently tucking your chin down.",
          "Bend the knee again while looking slightly up.",
          "Move rhythmically and never hold at the end of range.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets, once or twice a day",
        difficulty: "Beginner",
        suitableFor: "Nerve related leg symptoms that are settling.",
        safety: "This should not increase your leg pain. Reduce the range if it does.",
      },
      {
        name: "Prone Press Up",
        startingPosition: "Lying face down with hands under your shoulders.",
        steps: [
          "Press your upper body up while keeping your hips down.",
          "Only go as far as symptoms allow.",
          "Lower slowly and repeat.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 to 3 sets",
        difficulty: "Beginner",
        suitableFor: "People whose leg symptoms improve with back extension.",
        safety: "Stop if symptoms travel further down the leg during the exercise.",
      },
    ],
    whenToStop: [
      "Symptoms moving further down the leg rather than closer to the back.",
      "Increasing numbness or weakness in the foot.",
    ],
    whenToSeePhysio:
      "Get assessed if leg pain lasts longer than a week, is worsening, or is disturbing sleep. Seek emergency care for numbness around the groin or buttocks, difficulty passing urine, or rapidly progressing leg weakness.",
    faqs: [
      { q: "How long does sciatica take to settle?", a: "Many cases improve substantially within six to twelve weeks, although timelines vary with severity and how long symptoms have been present." },
      { q: "Should I rest or stay active with sciatica?", a: "Gentle activity within comfort is generally recommended. Complete rest tends to stiffen the back and slow recovery." },
      { q: "Do I need surgery for sciatica?", a: "The large majority of cases do not require surgery. It is usually reserved for persistent severe symptoms or significant nerve compromise." },
      { q: "Can physiotherapy help sciatica?", a: "It often does, by reducing nerve sensitivity, restoring movement and rebuilding strength, alongside advice about daily loading." },
    ],
    relatedServices: ["back-pain-physiotherapy", "dry-needling", "home-physiotherapy"],
    relatedPosts: [
      "exercises-for-sciatica",
      "exercises-to-avoid-with-sciatica",
      "back-pain-causes-treatment-and-physiotherapy-exercises",
    ],
  },
  {
    slug: "exercises-for-sciatica",
    category: "Physiotherapy Exercises",
    pillarSlug: "sciatica-causes-treatment-and-exercises",
    publishedAt: "2026-08-02",
    updatedAt: UPDATED,
    title: "Exercises and Stretches for Sciatic Nerve Pain",
    metaTitle: "Exercises for Sciatica | Nerve Glides and Safe Stretches",
    metaDescription:
      "Physiotherapist explained exercises for sciatic nerve pain, including nerve glides, glute stretches and how to judge whether an exercise is helping.",
    quickAnswer:
      "Use the centralisation rule: exercises that move symptoms out of the leg and closer to the back are usually helping, and exercises that push symptoms further down the leg should be stopped or modified.",
    intro:
      "With sciatica the choice of exercise matters less than the response to it. This guide gives a small set of options and a clear rule for judging whether each one is right for you.",
    tags: ["sciatica exercises", "nerve glide", "piriformis"],
    sections: [
      {
        heading: "The centralisation rule",
        body:
          "Track where your symptoms sit before and after each exercise. Movement of pain from the calf into the thigh, or from the thigh into the buttock, generally means the nerve is becoming less irritated, even if the back feels temporarily more sore. Symptoms travelling further down mean stop.",
      },
      {
        heading: "Build tolerance gradually",
        body:
          "Start with fewer repetitions than you think you need and repeat several times through the day rather than one long session. As symptoms settle, add walking and then progressive strengthening for the hips and trunk.",
      },
    ],
    exercises: [
      {
        name: "Supine Nerve Glide",
        startingPosition: "Lying on your back holding behind one thigh with the hip bent.",
        steps: [
          "Slowly straighten the knee until you feel a light pull.",
          "Bend the knee again immediately.",
          "Keep the movement smooth without holding at the end.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Nerve related tightness in the back of the leg.",
        safety: "Never stretch a nerve into pain. Ease the range instead.",
      },
      {
        name: "Piriformis Stretch",
        startingPosition: "Lying on your back with one ankle crossed over the opposite knee.",
        steps: [
          "Pull the supporting thigh gently towards your chest.",
          "Feel a stretch deep in the buttock.",
          "Breathe normally throughout.",
        ],
        duration: "20 to 30 seconds",
        sets: "2 each side",
        difficulty: "Beginner",
        suitableFor: "Buttock tightness alongside settling leg symptoms.",
        safety: "Stop if the stretch reproduces sharp shooting pain down the leg.",
      },
      {
        name: "Walking Programme",
        startingPosition: "Comfortable shoes on a flat route.",
        steps: [
          "Walk at an easy pace for a duration you know you tolerate.",
          "Add one to two minutes every few days as symptoms allow.",
          "Break longer walks into shorter sessions if needed.",
        ],
        duration: "Start with 5 to 10 minutes",
        sets: "1 to 3 times daily",
        difficulty: "Beginner",
        suitableFor: "Almost everyone recovering from sciatica.",
        safety: "Reduce the duration if leg symptoms increase over the following day.",
      },
    ],
    whenToStop: [
      "Pain spreading further down the leg during or after the exercise.",
      "New foot weakness such as catching your toes when walking.",
    ],
    whenToSeePhysio:
      "If symptoms are not improving after two to three weeks, or are limiting sleep and work, book an assessment so exercises can be matched to your response pattern.",
    faqs: [
      { q: "What exercises should I avoid with sciatica?", a: "Anything that consistently pushes symptoms further down the leg, and often aggressive hamstring stretching or heavy loaded bending early on." },
      { q: "Are nerve glides supposed to hurt?", a: "No. They should produce a light pull at most. Pain means the range is too large." },
      { q: "How often should I do sciatica exercises?", a: "Small sessions several times a day usually work better than one long session." },
      { q: "Is walking good for sciatica?", a: "For most people yes, provided the duration is built up gradually and symptoms settle afterwards." },
    ],
    relatedServices: ["back-pain-physiotherapy", "dry-needling"],
    relatedPosts: [
      "sciatica-causes-treatment-and-exercises",
      "exercises-to-avoid-with-sciatica",
      "exercises-for-lower-back-pain",
    ],
  },
  {
    slug: "exercises-to-avoid-with-sciatica",
    category: "Sciatica",
    pillarSlug: "sciatica-causes-treatment-and-exercises",
    publishedAt: "2026-08-03",
    updatedAt: UPDATED,
    title: "What Exercises Should You Avoid With Sciatica?",
    metaTitle: "Exercises to Avoid With Sciatica | Physiotherapist Advice",
    metaDescription:
      "Which movements commonly aggravate sciatic nerve pain, safer alternatives to use instead and how to judge when you can reintroduce them.",
    quickAnswer:
      "Avoid movements that repeatedly push symptoms further down the leg. Early on these often include aggressive hamstring stretching, heavy loaded bending, prolonged sitting and high impact activity. Most can be reintroduced gradually once symptoms centralise.",
    intro:
      "There is no universal blacklist for sciatica. There are, however, movements that commonly aggravate an irritated nerve root in the early stages, and knowing them saves weeks of frustration.",
    tags: ["sciatica", "avoid", "aggravating factors"],
    sections: [
      {
        heading: "Commonly aggravating movements",
        body:
          "Deep forward bending under load, heavy deadlifts and seated hamstring stretching all place tension on an already sensitive nerve. Long periods of sitting, especially in a slumped position or driving, are another frequent aggravator. High impact running early in a flare up can also increase symptoms.",
      },
      {
        heading: "Safer alternatives while you recover",
        body:
          "Replace loaded bending with hip hinge patterns at light load or hip bridges. Replace static hamstring stretching with gentle nerve glides. Replace long drives with breaks every 30 to 45 minutes, and replace running with walking or cycling if those feel comfortable.",
      },
      {
        heading: "How to reintroduce them",
        body:
          "Once symptoms have settled towards the back and buttock and are consistent day to day, reintroduce one activity at a time at reduced load. Judge the response over the following 24 hours before progressing further.",
      },
    ],
    whenToStop: [
      "Symptoms extending further down the leg.",
      "Foot weakness, numbness or difficulty lifting the toes.",
    ],
    whenToSeePhysio:
      "A physiotherapist can test which movements ease your symptoms and which provoke them, which makes the avoid list far shorter and more specific to you.",
    faqs: [
      { q: "Can I go to gym with sciatica?", a: "Often yes, with modified exercise selection and load. Many people continue upper body and controlled lower body work throughout." },
      { q: "Is stretching my hamstring bad for sciatica?", a: "Aggressive hamstring stretching can provoke an irritated nerve. Gentle nerve gliding is usually better tolerated early on." },
      { q: "Should I avoid sitting completely?", a: "No, but break up long sitting periods and use a supportive position that keeps your symptoms settled." },
      { q: "When can I run again?", a: "Usually once leg symptoms have settled and walking is comfortable, then reintroduce running gradually with a walk run pattern." },
    ],
    relatedServices: ["back-pain-physiotherapy"],
    relatedPosts: [
      "sciatica-causes-treatment-and-exercises",
      "exercises-for-sciatica",
      "back-pain-causes-treatment-and-physiotherapy-exercises",
    ],
  },

  // ===================== KNEE CLUSTER =====================
  {
    slug: "knee-pain-causes-treatment-and-exercises",
    category: "Knee Pain",
    isPillar: true,
    featured: true,
    publishedAt: "2026-08-01",
    updatedAt: UPDATED,
    title: "Knee Pain: Causes, Treatment and Physiotherapy Exercises",
    metaTitle: "Knee Pain: Causes, Treatment and Exercises | Physio Centurion",
    metaDescription:
      "A physiotherapist's guide to knee pain, covering common causes, treatment options, strengthening exercises and when to book an assessment.",
    quickAnswer:
      "Most knee pain in active adults comes from load related irritation of the kneecap, tendons or joint surfaces rather than structural damage. Progressive strengthening of the quadriceps, glutes and calves, combined with sensible training load, is the core of treatment.",
    intro:
      "Knees complain when the load applied exceeds the load they are prepared for. That principle explains everything from runner's knee to arthritic joint pain, and it also explains why strengthening is the mainstay of treatment.",
    tags: ["knee", "patellofemoral", "arthritis", "quadriceps"],
    sections: [
      {
        heading: "Common causes",
        body:
          "In younger and active people, pain around or under the kneecap, patellar or quadriceps tendon problems and meniscal irritation are most common. In older adults, osteoarthritis related pain and stiffness dominate. Acute twisting injuries with immediate swelling may involve a ligament or meniscus and should be assessed promptly.",
      },
      {
        heading: "Treatment principles",
        body:
          "Physiotherapy focuses on offloading the irritated structure in the short term while building strength and capacity for the long term. That usually means adjusting training volume, adding progressive resistance exercise two to three times a week, and addressing hip and calf strength, which strongly influence knee load.",
      },
      {
        heading: "Knee osteoarthritis",
        body:
          "Exercise remains a first line treatment for knee osteoarthritis. Strengthening and regular low impact activity may reduce pain and improve function. Movement does not wear the joint out, and staying active protects both the knee and general health.",
      },
    ],
    exercises: [
      {
        name: "Isometric Wall Sit",
        startingPosition: "Back against a wall, feet a comfortable distance forwards.",
        steps: [
          "Slide down until your knees are bent to a comfortable angle.",
          "Hold the position while breathing normally.",
          "Stand up and rest between holds.",
        ],
        duration: "20 to 45 second holds",
        sets: "4 to 5 holds",
        difficulty: "Beginner",
        suitableFor: "Reducing pain in irritable knees while maintaining strength.",
        safety: "Choose a depth that keeps pain at or below three out of ten.",
      },
      {
        name: "Step Ups",
        startingPosition: "Standing in front of a low step.",
        steps: [
          "Place one foot fully on the step.",
          "Press through that heel to stand up tall.",
          "Lower slowly under control.",
        ],
        reps: "8 to 12 per side",
        sets: "3 sets",
        difficulty: "Intermediate",
        suitableFor: "Building functional strength for stairs and walking.",
        safety: "Use a lower step if the knee complains, and hold a rail for balance.",
      },
      {
        name: "Calf Raises",
        startingPosition: "Standing with hands lightly on a wall for balance.",
        steps: [
          "Rise onto the balls of both feet.",
          "Pause briefly at the top.",
          "Lower slowly over three seconds.",
        ],
        reps: "12 to 15 repetitions",
        sets: "3 sets",
        difficulty: "Beginner",
        suitableFor: "Supporting knee load through the lower leg.",
        safety: "Progress to single leg only when double leg is comfortable.",
      },
    ],
    whenToStop: [
      "Pain above three out of ten during the exercise.",
      "Knee swelling that increases after activity.",
      "Giving way, locking or an inability to fully straighten the knee.",
    ],
    whenToSeePhysio:
      "Book an assessment for knee pain lasting more than two weeks, recurrent swelling, instability, or knee pain that limits stairs, walking or sport.",
    faqs: [
      { q: "Is it safe to exercise with knee pain?", a: "Usually yes, with the right exercise selection and load. Mild discomfort that settles quickly is generally acceptable." },
      { q: "Does running damage the knees?", a: "Evidence does not support the idea that recreational running damages healthy knees. Sudden increases in training load are a bigger risk factor." },
      { q: "Should I use ice or heat for knee pain?", a: "Ice may help an acutely swollen knee, while heat often suits stiff, arthritic knees. Use whichever gives you relief." },
      { q: "How long does knee rehab take?", a: "Load related knee pain often improves within six to twelve weeks of consistent strengthening, depending on the diagnosis." },
    ],
    relatedServices: ["orthopaedic-rehabilitation", "sports-injury-physiotherapy", "post-operative-rehabilitation"],
    relatedPosts: [
      "exercises-for-knee-pain",
      "exercises-for-runners-knee",
      "common-running-injuries",
    ],
  },
  {
    slug: "exercises-for-knee-pain",
    category: "Physiotherapy Exercises",
    pillarSlug: "knee-pain-causes-treatment-and-exercises",
    publishedAt: "2026-08-02",
    updatedAt: UPDATED,
    title: "Knee Strengthening Exercises for Knee Pain",
    metaTitle: "Exercises for Knee Pain | Strengthening Routine From a Physio",
    metaDescription:
      "A progressive knee strengthening routine with clear instructions, dosage and pain rules, suitable for many common causes of knee pain.",
    quickAnswer:
      "Train two to three times a week using pain as a guide: keep discomfort at or below three out of ten during exercise and settled within 24 hours. Start with isometric holds and controlled bodyweight work, then progress load gradually.",
    intro:
      "Strength is the most reliable long term treatment for load related knee pain. This routine progresses from calming an irritable knee through to building genuine capacity.",
    tags: ["knee exercises", "quadriceps", "strengthening"],
    sections: [
      {
        heading: "Stage one: settle the irritation",
        body:
          "Isometric holds such as wall sits and static quadriceps contractions often reduce pain while maintaining muscle activity. Use these daily during a flare, alongside a temporary reduction in aggravating activity rather than complete rest.",
      },
      {
        heading: "Stage two: build strength",
        body:
          "Progress to controlled movement such as step ups, split squats and leg presses within a comfortable range. Aim for three sets of eight to twelve repetitions at an effort where the last two repetitions are hard but achievable with good technique.",
      },
      {
        heading: "Stage three: return to your sport",
        body:
          "Add speed, direction change and impact gradually once strength is comparable between sides. Rushing this stage is the most common reason knee pain returns.",
      },
    ],
    exercises: [
      {
        name: "Static Quadriceps Contraction",
        startingPosition: "Sitting with your leg straight and a small towel roll under the knee.",
        steps: [
          "Press the back of your knee gently down into the roll.",
          "Feel the thigh muscle tighten.",
          "Hold for five to ten seconds, then relax.",
        ],
        reps: "10 repetitions",
        sets: "2 to 3 sets daily",
        difficulty: "Beginner",
        suitableFor: "Very painful or recently injured knees.",
        safety: "Should be comfortable. Stop if it produces sharp pain.",
      },
      {
        name: "Split Squat",
        startingPosition: "Standing in a staggered stance, back heel lifted.",
        steps: [
          "Lower straight down until the back knee approaches the floor.",
          "Keep your trunk upright and the front knee tracking over the foot.",
          "Press back up through the front heel.",
        ],
        reps: "8 to 10 per side",
        sets: "3 sets",
        difficulty: "Intermediate",
        suitableFor: "Building single leg strength once pain is manageable.",
        safety: "Reduce the depth rather than pushing into pain.",
      },
      {
        name: "Sideways Band Walk",
        startingPosition: "Standing with a resistance band around the thighs, knees slightly bent.",
        steps: [
          "Step sideways keeping tension in the band.",
          "Take ten steps in one direction, then return.",
          "Keep your feet pointing forwards.",
        ],
        reps: "10 steps each way",
        sets: "3 sets",
        difficulty: "Beginner",
        suitableFor: "Strengthening the hip muscles that control knee position.",
        safety: "Stop if you feel pinching at the front of the hip.",
      },
    ],
    whenToStop: [
      "Pain above three out of ten during the exercise.",
      "Swelling increasing after sessions.",
      "Knee giving way or locking.",
    ],
    whenToSeePhysio:
      "If your knee is not improving after four to six weeks of consistent strengthening, or if there is swelling, instability or locking, book an assessment.",
    faqs: [
      { q: "How many times a week should I do knee exercises?", a: "Two to three strength sessions a week is a common target, with lighter isometric work daily during a flare." },
      { q: "Are squats bad for the knees?", a: "Squats are not inherently harmful. Depth, load and progression rate determine whether they irritate a sensitive knee." },
      { q: "Should knee exercises hurt?", a: "Mild discomfort up to about three out of ten that settles within a day is generally acceptable." },
      { q: "Do I need equipment?", a: "Not to start. Bodyweight, a step and a resistance band cover most early rehab." },
    ],
    relatedServices: ["orthopaedic-rehabilitation", "post-operative-rehabilitation"],
    relatedPosts: [
      "knee-pain-causes-treatment-and-exercises",
      "exercises-for-runners-knee",
      "common-running-injuries",
    ],
  },
  {
    slug: "exercises-for-runners-knee",
    category: "Running Injuries",
    pillarSlug: "knee-pain-causes-treatment-and-exercises",
    publishedAt: "2026-08-03",
    updatedAt: UPDATED,
    title: "Runner's Knee: Why It Happens and Exercises That May Help",
    metaTitle: "Runner's Knee Exercises | Physiotherapy Guide Centurion",
    metaDescription:
      "Understand runner's knee, how to modify your training while it settles, and a progressive exercise routine targeting the hips, quads and calves.",
    quickAnswer:
      "Runner's knee usually reflects more load through the kneecap joint than it is currently prepared for. Reduce running volume temporarily rather than stopping, strengthen the quadriceps, glutes and calves, and rebuild mileage by roughly ten percent per week.",
    intro:
      "Pain at the front of the knee that appears a few kilometres into a run, worsens on downhills and aches after sitting is one of the most familiar patterns in running injuries.",
    tags: ["runners knee", "patellofemoral pain", "running"],
    sections: [
      {
        heading: "What is going on",
        body:
          "Patellofemoral pain, commonly called runner's knee, involves irritation of the joint between the kneecap and thigh bone. It typically follows a change in training: more distance, more hills, faster sessions or new shoes. Strength deficits in the quadriceps, glutes and calves increase the load the joint has to absorb with each stride.",
      },
      {
        heading: "Adjusting your running",
        body:
          "Complete rest is rarely necessary. Reduce weekly distance to a level that keeps pain at or below three out of ten during and after running, avoid steep downhills initially, and consider slightly increasing your step rate, which may reduce load at the knee. Rebuild volume gradually once symptoms settle.",
      },
      {
        heading: "Running physiotherapy in Centurion",
        body:
          "If you are training for a race and knee pain keeps interrupting your build up, a running assessment can look at your strength, training load and mechanics together rather than in isolation.",
      },
    ],
    exercises: [
      {
        name: "Spanish Squat",
        startingPosition: "Standing with a resistance band looped behind your knees and anchored in front.",
        steps: [
          "Lean back into the band and bend your knees.",
          "Keep your shins fairly vertical and your trunk upright.",
          "Hold the position, then return to standing.",
        ],
        duration: "30 to 45 second holds",
        sets: "4 to 5 holds",
        difficulty: "Intermediate",
        suitableFor: "Reducing kneecap pain while loading the quadriceps.",
        safety: "Adjust depth so pain stays at or below three out of ten.",
      },
      {
        name: "Single Leg Calf Raise",
        startingPosition: "Standing on one foot with light hand support.",
        steps: [
          "Rise onto the ball of the foot.",
          "Pause at the top.",
          "Lower slowly over three seconds.",
        ],
        reps: "10 to 15 repetitions",
        sets: "3 sets",
        difficulty: "Intermediate",
        suitableFor: "Runners, since calf strength absorbs a large share of running load.",
        safety: "Start with both legs if single leg is too demanding.",
      },
      {
        name: "Hip Abduction Side Lying",
        startingPosition: "Lying on your side with legs straight and stacked.",
        steps: [
          "Lift the top leg towards the ceiling.",
          "Keep the leg slightly behind your body and toes facing forwards.",
          "Lower slowly.",
        ],
        reps: "12 to 15 per side",
        sets: "3 sets",
        difficulty: "Beginner",
        suitableFor: "Strengthening the gluteal muscles that control the knee.",
        safety: "Avoid rolling backwards to make the lift easier.",
      },
    ],
    whenToStop: [
      "Knee pain above three out of ten during a run.",
      "Pain that is worse the morning after running.",
      "Swelling at the front of the knee.",
    ],
    whenToSeePhysio:
      "If pain persists beyond three to four weeks of load reduction and strengthening, or keeps returning each time you increase mileage, get assessed.",
    faqs: [
      { q: "Can I keep running with runner's knee?", a: "Often yes, at reduced volume and intensity, provided pain stays low during and after the run." },
      { q: "Do I need new running shoes?", a: "Shoes are one small factor. Comfort matters most, and shoe changes rarely fix a load related problem on their own." },
      { q: "How long does runner's knee take to settle?", a: "Many runners improve within six to twelve weeks with consistent strengthening and sensible load management." },
      { q: "Is a knee strap useful?", a: "Some runners find a patellar strap reduces symptoms during running. It is a comfort aid rather than a treatment." },
    ],
    relatedServices: ["sports-injury-physiotherapy", "orthopaedic-rehabilitation"],
    relatedPosts: [
      "knee-pain-causes-treatment-and-exercises",
      "common-running-injuries",
      "how-to-prevent-running-injuries",
      "exercises-for-knee-pain",
    ],
  },

  // ===================== SHOULDER =====================
  {
    slug: "shoulder-pain-causes-treatment-and-exercises",
    category: "Shoulder Pain",
    isPillar: true,
    publishedAt: "2026-08-01",
    updatedAt: UPDATED,
    title: "Shoulder Pain: Causes, Treatment and Physiotherapy Exercises",
    metaTitle: "Shoulder Pain: Causes, Treatment and Exercises | Physio",
    metaDescription:
      "Common causes of shoulder pain, what physiotherapy treatment involves and a progressive rotator cuff exercise routine with dosage guidance.",
    quickAnswer:
      "Most shoulder pain in adults involves the rotator cuff tendons and how the shoulder blade moves. Progressive loading of the rotator cuff, combined with shoulder blade strength work, is more effective than rest for the majority of cases.",
    intro:
      "Shoulders are demanding joints: enormous mobility supported mostly by muscle. When the load exceeds what those muscles are prepared for, pain follows, usually on reaching, lifting or sleeping on that side.",
    tags: ["shoulder", "rotator cuff", "impingement", "frozen shoulder"],
    sections: [
      {
        heading: "Common causes",
        body:
          "Rotator cuff related shoulder pain is by far the most common, often described as impingement. Frozen shoulder produces marked stiffness in all directions and follows a longer course. Shoulder pain can also be referred from the neck, which is why the neck is assessed alongside the shoulder.",
      },
      {
        heading: "What treatment involves",
        body:
          "Treatment normally includes graded loading of the rotator cuff, work on shoulder blade control and mid back mobility, and advice on modifying aggravating activities such as overhead work or bench pressing. Manual therapy and dry needling may reduce symptoms in the short term.",
      },
      {
        heading: "Realistic timelines",
        body:
          "Rotator cuff related pain often improves substantially in six to twelve weeks with consistent loading. Frozen shoulder is slower and can take many months, although physiotherapy may help maintain movement and manage pain along the way.",
      },
    ],
    exercises: [
      {
        name: "External Rotation With a Band",
        startingPosition: "Standing with elbow at your side bent to ninety degrees, band anchored at waist height.",
        steps: [
          "Keep your elbow tucked in against your body.",
          "Rotate your forearm outwards away from your stomach.",
          "Return slowly under control.",
        ],
        reps: "10 to 12 repetitions",
        sets: "3 sets",
        difficulty: "Beginner",
        suitableFor: "Rotator cuff related shoulder pain.",
        safety: "Use a light band. Keep pain at or below three out of ten.",
      },
      {
        name: "Scaption Raise",
        startingPosition: "Standing with light weights or no weight, arms at your sides.",
        steps: [
          "Raise your arms at about forty five degrees between front and side.",
          "Lift only to a comfortable height.",
          "Lower slowly.",
        ],
        reps: "8 to 12 repetitions",
        sets: "2 to 3 sets",
        difficulty: "Intermediate",
        suitableFor: "Restoring pain free overhead reach.",
        safety: "Stop at the height where pain begins rather than pushing through.",
      },
      {
        name: "Wall Slide",
        startingPosition: "Forearms on a wall at shoulder height.",
        steps: [
          "Slide your forearms up the wall.",
          "Allow your shoulder blades to rotate naturally.",
          "Slide back down with control.",
        ],
        reps: "8 to 10 repetitions",
        sets: "2 sets",
        difficulty: "Beginner",
        suitableFor: "Improving shoulder blade movement with low load.",
        safety: "Avoid shrugging heavily into the neck.",
      },
    ],
    whenToStop: [
      "Sharp catching pain during the movement.",
      "Pain that keeps you awake more than usual that night.",
      "Sudden weakness or inability to lift the arm.",
    ],
    whenToSeePhysio:
      "See a physiotherapist if shoulder pain lasts more than three weeks, disturbs sleep, follows a fall, or is limiting work and sport.",
    faqs: [
      { q: "Should I rest a painful shoulder?", a: "Complete rest usually stiffens the shoulder. Modified activity with graded loading is generally more effective." },
      { q: "What is shoulder impingement?", a: "It is a descriptive term for pain on reaching overhead, most often related to rotator cuff tendon irritation and load." },
      { q: "How long does frozen shoulder last?", a: "It typically runs a prolonged course over many months. Physiotherapy focuses on maintaining movement and managing pain." },
      { q: "Can neck problems cause shoulder pain?", a: "Yes. Neck referred pain is common, which is why both areas are examined during assessment." },
    ],
    relatedServices: ["orthopaedic-rehabilitation", "post-operative-rehabilitation", "dry-needling"],
    relatedPosts: [
      "neck-pain-causes-treatment-and-exercises",
      "exercises-for-forward-head-posture",
      "exercises-for-neck-pain",
    ],
  },

  // ===================== RUNNING CLUSTER =====================
  {
    slug: "common-running-injuries",
    category: "Running Injuries",
    isPillar: true,
    featured: true,
    publishedAt: "2026-08-01",
    updatedAt: UPDATED,
    title: "Common Running Injuries and How Physiotherapy Helps",
    metaTitle: "Common Running Injuries | Causes and Physio Treatment",
    metaDescription:
      "The most common running injuries, why they happen, typical recovery timelines and how physiotherapy in Centurion can get you back to training.",
    quickAnswer:
      "Most running injuries are load related rather than caused by bad form. The usual suspects are runner's knee, Achilles tendinopathy, shin pain, plantar heel pain and hamstring or glute tendon problems. Managing weekly load and building strength addresses most of them.",
    intro:
      "Running injuries rarely appear out of nowhere. They usually follow a change in how much, how fast or how often you have been running. Recognising the pattern early keeps a niggle from becoming a season.",
    tags: ["running", "achilles", "shin splints", "plantar fasciitis"],
    sections: [
      {
        heading: "The injuries we see most",
        body:
          "Patellofemoral pain at the front of the knee, Achilles tendinopathy at the back of the ankle, medial tibial stress syndrome along the shin, plantar heel pain under the foot and proximal hamstring tendinopathy at the sit bone make up the large majority of running complaints in our Centurion clinic.",
      },
      {
        heading: "Why they happen",
        body:
          "The common thread is load. A sudden jump in weekly distance, adding hills or speed work too quickly, returning after a break at previous volumes, or running on tired legs from insufficient sleep and recovery all increase risk. Strength deficits, particularly in the calves and hips, reduce the load your tissues can absorb.",
      },
      {
        heading: "What treatment involves",
        body:
          "Assessment covers your training history, strength, mobility and running mechanics. Treatment then combines a temporary load adjustment, progressive strength work for the affected tissue and a structured return to running plan, often as a walk run progression.",
      },
    ],
    whenToStop: [
      "Pain that increases during a run rather than warming up.",
      "Limping or altering your running form to cope.",
      "Pain in the same spot on consecutive runs that is worsening.",
    ],
    whenToSeePhysio:
      "See a physiotherapist if a niggle persists for more than a week or two, if you are altering your gait to run, or if you have a race approaching and need a plan that protects your training block.",
    faqs: [
      { q: "Should I stop running completely when injured?", a: "Usually not. Most runners can maintain some running at reduced volume, which supports both fitness and tissue tolerance." },
      { q: "How quickly can I increase my mileage?", a: "Around ten percent per week is a widely used guide, adjusted for how your body responds." },
      { q: "Do running shoes prevent injury?", a: "Comfortable shoes matter, but training load and strength are far stronger predictors of injury risk." },
      { q: "When should a runner see a physiotherapist?", a: "Early. A niggle addressed in week one is far quicker to resolve than one carried for two months." },
    ],
    relatedServices: ["sports-injury-physiotherapy", "dry-needling", "orthopaedic-rehabilitation"],
    relatedPosts: [
      "exercises-for-runners",
      "how-to-prevent-running-injuries",
      "exercises-for-runners-knee",
    ],
  },
  {
    slug: "exercises-for-runners",
    category: "Injury Prevention",
    pillarSlug: "common-running-injuries",
    publishedAt: "2026-08-02",
    updatedAt: UPDATED,
    title: "Strength Exercises for Runners",
    metaTitle: "Strength Exercises for Runners | Physio Designed Routine",
    metaDescription:
      "A twice weekly strength routine for runners targeting calves, glutes and hamstrings, with sets, repetitions and progression guidance.",
    quickAnswer:
      "Two short strength sessions a week, focused on calves, glutes and hamstrings, cover most of what runners need. Progress load or repetitions every one to two weeks and keep sessions on easier running days.",
    intro:
      "Runners do not need a bodybuilding programme. They need enough strength in a handful of key areas to absorb repeated impact for hours at a time.",
    tags: ["running strength", "calf raises", "glutes"],
    sections: [
      {
        heading: "Where to put your effort",
        body:
          "Calves absorb the largest share of running load, particularly at faster paces. Glutes control the pelvis and knee position. Hamstrings manage the swing phase of the stride. A routine covering these three areas addresses most running related weaknesses.",
      },
      {
        heading: "How to fit it into a training week",
        body:
          "Place strength sessions on the same day as an easy run or immediately after a run, keeping at least one full day before a hard session. Twenty to thirty minutes twice a week is enough to make a measurable difference over a season.",
      },
    ],
    exercises: [
      {
        name: "Single Leg Calf Raise",
        startingPosition: "Standing on one foot, light hand support for balance.",
        steps: [
          "Rise onto the ball of the foot as high as possible.",
          "Pause at the top for one second.",
          "Lower over three seconds.",
        ],
        reps: "12 to 20 repetitions",
        sets: "3 sets",
        difficulty: "Intermediate",
        suitableFor: "All runners, especially those with calf or Achilles history.",
        safety: "Stop if the Achilles becomes sore the following morning.",
      },
      {
        name: "Romanian Deadlift",
        startingPosition: "Standing with a light weight in both hands, knees softly bent.",
        steps: [
          "Push your hips backwards while keeping your back long.",
          "Lower the weight until you feel a hamstring stretch.",
          "Drive your hips forwards to stand tall.",
        ],
        reps: "8 to 10 repetitions",
        sets: "3 sets",
        difficulty: "Intermediate",
        suitableFor: "Hamstring and posterior chain strength.",
        safety: "Keep the load light while you learn the hip hinge pattern.",
      },
      {
        name: "Single Leg Glute Bridge",
        startingPosition: "Lying on your back, one knee bent, the other leg extended.",
        steps: [
          "Press through the heel of the bent leg to lift your hips.",
          "Keep your pelvis level throughout.",
          "Lower slowly.",
        ],
        reps: "8 to 12 per side",
        sets: "3 sets",
        difficulty: "Intermediate",
        suitableFor: "Glute strength and pelvic control for running.",
        safety: "Stop if you get hamstring cramping and shorten the range.",
      },
    ],
    whenToStop: [
      "Sharp pain in a tendon during the exercise.",
      "Soreness that is worse the next morning and lasts beyond 24 hours.",
    ],
    whenToSeePhysio:
      "If a specific area keeps flaring despite consistent strength work, an assessment can target the exact tissue and load it appropriately.",
    faqs: [
      { q: "Will strength training make me slower?", a: "No. Strength work is generally associated with improved running economy and lower injury rates." },
      { q: "How heavy should I lift?", a: "Heavy enough that the last two repetitions are genuinely challenging with good technique." },
      { q: "Can I strength train during race week?", a: "Yes, but reduce volume significantly and avoid new exercises close to race day." },
      { q: "How long before I notice benefits?", a: "Strength changes usually become noticeable after six to eight weeks of consistent training." },
    ],
    relatedServices: ["sports-injury-physiotherapy"],
    relatedPosts: [
      "common-running-injuries",
      "how-to-prevent-running-injuries",
      "exercises-for-runners-knee",
    ],
  },
  {
    slug: "how-to-prevent-running-injuries",
    category: "Injury Prevention",
    pillarSlug: "common-running-injuries",
    publishedAt: "2026-08-03",
    updatedAt: UPDATED,
    title: "How to Prevent Running Injuries",
    metaTitle: "How to Prevent Running Injuries | Physiotherapist Guide",
    metaDescription:
      "Practical injury prevention for runners: managing training load, sleep and recovery, strength work and how to respond to early warning niggles.",
    quickAnswer:
      "Progress weekly distance gradually, keep most running easy, strength train twice a week, prioritise sleep, and act on niggles within the first week rather than running through them.",
    intro:
      "Injury prevention for runners is unglamorous: consistent easy running, sensible progressions and a bit of strength work. The runners who stay healthy are rarely the ones doing anything clever.",
    tags: ["prevention", "training load", "recovery"],
    sections: [
      {
        heading: "Manage the load",
        body:
          "Increase weekly distance gradually, commonly around ten percent, and avoid adding distance, hills and speed in the same week. After a break, restart below your previous volume and build back rather than resuming where you stopped.",
      },
      {
        heading: "Keep easy runs easy",
        body:
          "A large share of running should be at a pace where you can hold a conversation. Running every session at a moderately hard effort accumulates fatigue without the benefits of either easy or genuinely hard training.",
      },
      {
        heading: "Recovery is part of training",
        body:
          "Sleep is the most powerful recovery tool available. Insufficient sleep and periods of high life stress are consistently associated with higher injury rates in athletes. Fuel adequately, particularly around long runs.",
      },
      {
        heading: "Respond to niggles early",
        body:
          "A niggle that alters your stride or persists across several runs deserves attention within the week. Early intervention usually costs a few easy days. Ignoring it often costs several weeks.",
      },
    ],
    whenToStop: [
      "Pain that changes your running form.",
      "Pain that worsens during the run.",
      "Localised bone pain, which needs prompt assessment.",
    ],
    whenToSeePhysio:
      "Book a screening before a big training block, or as soon as a niggle appears. If you are training for Comrades, Two Oceans or a first marathon, an early plan protects the whole block.",
    faqs: [
      { q: "How much should I increase my mileage each week?", a: "Around ten percent is a useful guide, adjusted for how you feel and your training history." },
      { q: "Does stretching prevent running injuries?", a: "Static stretching has limited evidence for injury prevention. Strength training and load management have stronger support." },
      { q: "Should I run every day?", a: "Not necessarily. Most recreational runners do well on three to five runs a week with rest or cross training in between." },
      { q: "Is a warm up necessary?", a: "A short easy jog and some dynamic movement prepares the tissues and is worth the five minutes." },
    ],
    relatedServices: ["sports-injury-physiotherapy"],
    relatedPosts: [
      "common-running-injuries",
      "exercises-for-runners",
      "exercises-for-runners-knee",
    ],
  },
];
